import { useCallback, useState } from 'react';
import { createGroup } from '../../utils/groupUtils.ts';
import type { UnifiedDrawerActionsProps } from '../types.ts';

export const useDrawerActions = ({
  drawerState,
  setDrawerState,
  values,
  segments,
  hasSelectedItemsChanged,
  enforceMarketUnionAfterApply,
}: UnifiedDrawerActionsProps) => {
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  const onClose = useCallback(() => {
    if (hasSelectedItemsChanged) {
      setShowDiscardModal(true);
    } else {
      setDrawerState({ isOpen: false });
    }
  }, [hasSelectedItemsChanged, setDrawerState]);

  const onApply = useCallback(() => {
    const group = createGroup({
      baseValues: values,
      segments: {
        directSegments: segments.direct ?? [],
        rtgSegments: segments.rtg ?? [],
        thirdPartySegments: segments.thirdParty ?? [],
        siteCategorySegments: segments.siteCategory ?? [],
        oxPurchaseSegments: segments.oxPurchase ?? [],
        lookalikeSegments: values?.lookalike?.segments ?? [],
      },
    });

    const hasAnySegments = [
      segments.direct,
      segments.rtg,
      segments.thirdParty,
      segments.siteCategory,
      segments.oxPurchase,
      values?.lookalike?.segments,
    ].some(segmentArray => segmentArray && segmentArray.length > 0);

    if (!hasAnySegments) {
      drawerState.onShrinkSection();
    } else {
      drawerState.onExpandSection();
    }

    drawerState.updater(group);

    setDrawerState({ isOpen: false });

    enforceMarketUnionAfterApply?.(drawerState.fieldPath);
  }, [
    drawerState,
    setDrawerState,
    values,
    segments,
    drawerState.onShrinkSection,
    drawerState.onExpandSection,
    enforceMarketUnionAfterApply,
  ]);

  const onDiscardConfirm = useCallback(() => {
    setShowDiscardModal(false);
    setDrawerState({ isOpen: false });
  }, [setDrawerState]);

  const onDiscardCancel = useCallback(() => {
    setShowDiscardModal(false);
  }, []);

  return {
    showDiscardModal,
    onApply,
    onClose,
    onDiscardConfirm,
    onDiscardCancel,
  };
};
